import { motion } from "framer-motion";
import { BedDouble, Tent, Sparkles, Flame, ExternalLink, Sailboat, Bike, TreePine } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const STAYS = [
  { icon: BedDouble, label: "Luxury Cabins" },
  { icon: Tent, label: "Glamping Safari Tents" },
  { icon: Sparkles, label: "Geodesic Domes" },
  { icon: Flame, label: "Campsites & Campfires" },
];

const ACTIVITIES = [
  { icon: Bike, title: "ATV Trail Adventures", text: "Ride scenic trails through rugged Texas terrain" },
  { icon: Sailboat, title: "Speed Boat on Lake Texoma", text: "Private boat tours across open lake waters" },
  { icon: Flame, title: "Campfire Evenings", text: "S'mores, blankets, and stars over the village" },
  { icon: TreePine, title: "Eisenhower State Park", text: "Hiking and nature minutes from the mandir" },
];

export default function StayAndExplore() {
  return (
    <section id="stay" data-testid="stay-section" className="py-20 sm:py-28 bg-[#F5F0E6] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
          >
            <p className="eyebrow mb-4">11 · Stay &amp; Explore</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-amber-950" data-testid="stay-heading">
              Come for Aarti. Stay the Weekend.
            </h2>
            <div className="jali-divider my-6 w-48" />
            <p className="text-base leading-relaxed text-stone-700 mb-5">
              The mandir sits within <span className="font-semibold text-amber-900">Serenity Village at Texoma Resorts</span> —
              our own retreat campus near the shores of Lake Texoma. Devotees and visitors can turn a Saturday
              satsang into a whole weekend of peace: stay overnight right here on campus, then wake to birdsong
              and morning silence.
            </p>

            <div className="grid grid-cols-2 gap-3 mb-7">
              {STAYS.map((s, i) => (
                <motion.div key={s.label}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`stay-type-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center gap-3 bg-white rounded-xl border border-amber-900/10 px-4 py-3.5 hover:border-amber-600/40 transition-colors duration-300"
                >
                  <s.icon className="h-5 w-5 text-orange-700 shrink-0" />
                  <span className="text-sm font-medium text-stone-800">{s.label}</span>
                </motion.div>
              ))}
            </div>

            <p className="text-sm text-stone-600 leading-relaxed mb-7">
              And when the heart is full, adventure waits outside the door:
            </p>
            <ul className="space-y-3.5 mb-9">
              {ACTIVITIES.map((a) => (
                <li key={a.title} className="flex items-start gap-3" data-testid={`activity-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <a.icon className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                  <p className="text-sm text-stone-700">
                    <span className="font-semibold text-stone-900">{a.title}</span>
                    <span className="text-stone-500"> — {a.text}</span>
                  </p>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-4">
              <a href="https://texomaresorts.com" target="_blank" rel="noopener noreferrer"
                data-testid="stay-book-cta"
                className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-amber-600 to-orange-700 px-7 py-3.5 text-sm font-semibold text-white shadow-xl shadow-amber-900/25 hover:-translate-y-0.5 hover:shadow-amber-900/40 transition-all duration-300">
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent group-hover:translate-x-full transition-transform duration-700 ease-out" />
                Book Your Stay — TexomaResorts.com <ExternalLink className="h-4 w-4" />
              </a>
              <a href="https://texomaresorts.com/experiences" target="_blank" rel="noopener noreferrer"
                data-testid="stay-experiences-cta"
                className="inline-flex items-center gap-2 rounded-full border border-amber-900/25 bg-white/60 px-7 py-3.5 text-sm font-semibold text-amber-900 hover:bg-white hover:-translate-y-0.5 transition-all duration-300">
                Explore Experiences <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
            className="grid grid-rows-[3fr_2fr] gap-4"
          >
            <div className="diya-glow rounded-2xl overflow-hidden border border-amber-900/15 shadow-2xl shadow-amber-900/10">
              <img src="/images/resort-village.jpg" alt="Serenity Village at Texoma Resorts — on-campus stays near Lake Texoma"
                data-testid="stay-image-main" className="relative z-10 w-full h-full min-h-[320px] object-cover hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="rounded-2xl overflow-hidden border border-amber-900/15 shadow-xl shadow-amber-900/10">
              <img src="/images/resort-detail.jpg" alt="Resort life at the spiritual village"
                data-testid="stay-image-detail" className="w-full h-full min-h-[200px] object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
